import React from "react";
import { motion } from "motion/react";
import { ChevronDown, Cpu, Eye, Layers } from "lucide-react";

interface SectionDividerProps {
  type: "light-to-light" | "light-to-dark" | "dark-to-dark" | "dark-to-light";
  fromColor?: string;
  toColor?: string;
}

export default function SectionDivider({ type, fromColor, toColor }: SectionDividerProps) {
  if (type === "light-to-light") {
    const from = fromColor || "bg-white";
    const to = toColor || "bg-[#FAFAFC]";
    return (
      <div className="relative w-full h-16 pointer-events-none overflow-hidden select-none z-10">
        <div className={`absolute inset-0 bg-gradient-to-b ${from} to-transparent opacity-80`} />
        <div className="absolute inset-x-0 top-1/4 bottom-1/4 backdrop-blur-[3px] bg-white/10" />
        <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-500/10 to-transparent" />
      </div>
    );
  }

  if (type === "light-to-dark") {
    // Elegant blend from Roomy (#FAFAFC) -> SiteForces (#18181B)
    return (
      <div className="relative w-full h-48 -my-24 pointer-events-none overflow-hidden select-none z-20 bg-gradient-to-b from-[#FAFAFC] to-[#18181B]">
        {/* Decorative micro lens flares */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/15 to-transparent" />

        {/* Ambient colored glowing backdrop */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[60px] rounded-full bg-indigo-500/10 blur-[40px]" />
        
        {/* Interactive layout content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          {/* Connecting Vertical Energy Ray */}
          <div className="w-px h-12 bg-gradient-to-b from-indigo-500/0 via-indigo-500/60 to-indigo-500/0" />

          {/* Core HUD Gateway */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3.5 px-4.5 py-1.5 rounded-full bg-zinc-950/90 border border-zinc-800/80 shadow-[0_12px_40px_-12px_rgba(0,0,0,0.6)] backdrop-blur-xl pointer-events-auto"
          >
            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
              <Cpu size={10} className="animate-spin-slow" />
            </div>
            
            <div className="flex flex-col">
              <span className="font-mono text-[9px] font-bold tracking-[0.22em] text-white uppercase leading-none">
                SYSTEM ROUTE // 01
              </span>
              <span className="font-mono text-[7.5px] tracking-wider text-zinc-400 uppercase mt-0.5 leading-none">
                Proptech to Resource Engine
              </span>
            </div>

            <div className="h-5 w-px bg-zinc-800" />

            <div className="flex items-center gap-1">
              <span className="font-mono text-[8px] text-zinc-500 uppercase leading-none">ACTIVE</span>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>
          </motion.div>

          <div className="w-px h-12 bg-gradient-to-b from-indigo-500/0 via-indigo-500/60 to-indigo-500/0" />
        </div>
      </div>
    );
  }

  if (type === "dark-to-dark") {
    // Highly cinematic, rich dark space transition between SiteForces (#18181B) -> ArrowEscape (#09090C)
    return (
      <div className="relative w-full h-48 -my-24 pointer-events-none overflow-hidden select-none z-20 bg-gradient-to-b from-[#18181B] to-[#09090C]">
        {/* Subtle top or bottom border lights */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/10 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500/15 to-transparent" />

        {/* Dynamic backing optical flare */}
        <div className="absolute top-1/2 left-1/3 -translate-y-1/2 w-80 h-10 rounded-full bg-purple-500/10 blur-[30px]" />
        <div className="absolute top-1/2 right-1/3 -translate-y-1/2 w-80 h-10 rounded-full bg-indigo-500/10 blur-[30px]" style={{ animationDelay: "1s" }} />

        {/* Interactive layout content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          {/* Vertical light thread */}
          <div className="w-px h-12 bg-gradient-to-b from-purple-500/0 via-purple-500/60 to-purple-500/0" />

          {/* Central Orbit Shift HUD Capsule */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3.5 px-4.5 py-1.5 rounded-full bg-black/95 border border-zinc-900/90 shadow-[0_15px_50px_rgba(0,0,0,0.8)] backdrop-blur-2xl pointer-events-auto"
          >
            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-400">
              <Layers size={10} />
            </div>
            
            <div className="flex flex-col">
              <span className="font-mono text-[9px] font-bold tracking-[0.22em] text-zinc-100 uppercase leading-none">
                ORBIT SHIFT // 02
              </span>
              <span className="font-mono text-[7.5px] tracking-wider text-zinc-500 uppercase mt-0.5 leading-none">
                Enterprise to Mobile Play Store
              </span>
            </div>

            <div className="h-5 w-px bg-zinc-800" />

            <div className="flex items-center gap-1.5">
              <span className="font-mono text-[8px] text-zinc-500 uppercase leading-none">DEEP CORE</span>
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-ping" />
            </div>
          </motion.div>

          <div className="w-px h-12 bg-gradient-to-b from-purple-500/0 via-purple-500/60 to-purple-500/0" />
        </div>
      </div>
    );
  }

  if (type === "dark-to-light") {
    // Flawless, rich descent from ArrowEscape deep dark (#09090C) back to light Careers slate (#FAFAFC)
    return (
      <div className="relative w-full h-48 -my-24 pointer-events-none overflow-hidden select-none z-20 bg-gradient-to-b from-[#09090C] to-[#FAFAFC]">
        {/* Glowing aura at top */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-150/15 to-transparent" />

        {/* Atmospheric backing glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[40px] rounded-full bg-emerald-500/5 blur-[25px]" />

        {/* Layout hub design */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          {/* Vertical alignment light beam */}
          <div className="w-px h-12 bg-gradient-to-b from-emerald-500/0 via-emerald-500/40 to-emerald-500/0" />

          {/* Output descent indicator */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3.5 px-4.5 py-1.5 rounded-full bg-zinc-950/95 border border-zinc-800/80 shadow-[0_12px_45px_rgba(0,0,0,0.6)] backdrop-blur-xl pointer-events-auto"
          >
            <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              <Eye size={10} />
            </div>
            
            <div className="flex flex-col">
              <span className="font-mono text-[9px] font-bold tracking-[0.22em] text-white uppercase leading-none">
                STREAM REACH // 03
              </span>
              <span className="font-mono text-[7.5px] tracking-wider text-zinc-400 uppercase mt-0.5 leading-none">
                Publishing to Core Team Talent
              </span>
            </div>

            <div className="h-5 w-px bg-zinc-800" />

            <div className="flex items-center gap-1 text-emerald-400">
              <ChevronDown size={11} className="animate-bounce" />
            </div>
          </motion.div>

          <div className="w-px h-12 bg-gradient-to-b from-emerald-500/0 via-emerald-500/40 to-emerald-500/0" />
        </div>
      </div>
    );
  }

  return null;
}
